/* eslint-disable max-lines-per-function */
import classNames from 'clsx';
import { equals, path } from 'ramda';
import React, { type ReactNode, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDomainRestrictionsForItems } from 'reactApp/appHelpers/appHelpers';
import {
    ALLOWED_INDEX_TITLE,
    ENABLE_FULL_RESPONSIVE,
    IS_BIZ_USER,
    IS_DOCUMENTS_DOMAIN,
    PUBLIC_SHOVE,
} from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { familyFeatureDocuments } from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import { overquotaAfterClear } from 'reactApp/appHelpers/featuresHelpers/features/overquotaAfterClear';
import { enabledPublicDownloadLimits } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { seoEnable } from 'reactApp/appHelpers/featuresHelpers/features/seoEnable';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import DownloadLimitsInformer from 'reactApp/components/DownloadLimitsInformer/DownloadLimitsInformer';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { getAlbumsBreadcrumbs } from 'reactApp/modules/albums/albums.selector';
import { getAllDocumentsBreadcrumbs, getAllDocumentsDomainBreadcrumbs } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { getFeatureFaces } from 'reactApp/modules/features/features.selectors';
import { setDomainFoldersFilterActive } from 'reactApp/modules/home/home.actions';
import { getCurrentFolderHome, getDomainFoldersFilterActive, getHomeBreadcrumbs } from 'reactApp/modules/home/home.selectors';
import { getIncomingPublicBreadcrumbs } from 'reactApp/modules/incomingPublic/incomingPublic.selectors';
import { getIntegrationQueryString } from 'reactApp/modules/integration/integration.selectors';
import { MediaSelectors } from 'reactApp/modules/media/media.selectors';
import { getPersonalDocumentsBreadcrumbs } from 'reactApp/modules/personalDocuments/personalDocuments.selectors';
import {
    getCurrentPublicFolder,
    getCurrentPublicItem,
    getFirstPublicWithDownloadLeftZero,
    getPublicBreadcrumbs,
} from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isIntegrationStorage } from 'reactApp/modules/storage/storage.helpers';
import { getBreadCrumbs } from 'reactApp/modules/storage/storage.selectors';
import { type ItemMountedShared, EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { getVigboBreadcrumbs } from 'reactApp/modules/vigbo/vigbo.selectors';
import { isWelcomePromoShow } from 'reactApp/modules/welcome/welcome.selectors';
import type { RootState } from 'reactApp/store';
import type { Kind } from 'reactApp/types/Tree';
import { AttachesTooltip } from 'reactApp/ui/AttachesTooltip/AttachesTooltip';
import { BreadCrumbs } from 'reactApp/ui/Breadcrumbs/Breadcrumbs';
import type { BreadcrumbItem } from 'reactApp/ui/Breadcrumbs/Breadcrumbs.types';
import {
    autodeleteCrumb,
    familyAlbumsCrumb,
    familyFoldersCrumb,
    favoritesCrumb,
    feedCrumb,
    galleryCrumb,
    recomendCrumb,
    trashbinCrumb,
} from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer.data';
import { FaceFilter } from 'reactApp/ui/FaceFilter/FaceFilterComponent';
import { FamilyInlineInformer } from 'reactApp/ui/FamilyInlineInformer/FamilyInlineInformer';
import { FilesCounter } from 'reactApp/ui/FilesCounter/FilesCounter';
import { FileCounterTextColors } from 'reactApp/ui/FilesCounter/FilesCounter.types';
import { OverquotaRestoreRequestedInfo } from 'reactApp/ui/OverquotaRestoreRequestedInfo/OverquotaRestoreRequestedInfo';
import { sendGa } from 'reactApp/utils/ga';

import styles from './BreadcrumbsContainer.css';

const sendAnalytics = (action: string, label?: string) => sendGa('breadcrumbs', action, label);

export interface BreadcrumbsListItem extends ItemMountedShared {
    name: string;
    id: string;
    kind: Kind;
    storage: EStorageType;
    weblink?: string;
    showWeblink?: boolean;
    showShare?: boolean;
    showIcon?: boolean;
    iconName?: string;
    icon?: ReactNode;
    href?: string;
    search?: string;
    parent?: string;
}

let prevBreadCrumbsList: BreadcrumbsListItem[] = [];

// TODO: use deepEqualSelector to prevent rerenders
// eslint-disable-next-line complexity
export const getBreadCrumbsList = (state: RootState, storage?: EStorageType, isSearch?: boolean): BreadcrumbsListItem[] => {
    const currentStorage = getCurrentStorage(state);
    const isTablet = MediaSelectors.isTablet(state);

    storage = storage || currentStorage;

    const isShared = IS_BIZ_USER
        ? storage === EStorageType.sharedAutodelete
        : storage === EStorageType.sharedLinks || storage === EStorageType.sharedAutodelete || storage === EStorageType.sharedIncoming;

    if (storage === EStorageType.feed) {
        return [
            {
                ...feedCrumb[0],
                name: IS_BIZ_USER ? 'Последние файлы' : 'Недавние',
            },
        ];
    } else if (storage === EStorageType.alldocuments) {
        return IS_DOCUMENTS_DOMAIN ? getAllDocumentsDomainBreadcrumbs(state) : getAllDocumentsBreadcrumbs(state);
    } else if (storage === EStorageType.recommend) {
        return recomendCrumb;
    } else if (storage === EStorageType.trashbin) {
        return trashbinCrumb;
    } else if (storage === EStorageType.favorites) {
        return favoritesCrumb;
    } else if (storage === EStorageType.home) {
        return getHomeBreadcrumbs(state);
    } else if (storage === EStorageType.attaches) {
        return [
            {
                kind: 'storage',
                name: isSearch ? 'Результаты поиска' : 'Из почты',
                id: '',
                storage: EStorageType.attaches,
                href: '/attaches',
            },
        ];
    } else if (storage === EStorageType.public && isTablet) {
        const item = getCurrentPublicItem(state);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return getBreadCrumbs(state, item?.id || undefined);
    } else if (storage === EStorageType.public) {
        return getPublicBreadcrumbs(state);
    } else if (storage === EStorageType.gallery) {
        return galleryCrumb;
    } else if (isShared) {
        return [
            {
                name: 'Совместные',
                id: '/',
                kind: 'storage',
                storage,
            },
        ];
    } else if (storage === EStorageType.sharedAutodelete) {
        return autodeleteCrumb;
    } else if (storage === EStorageType.documents) {
        return getPersonalDocumentsBreadcrumbs(state);
    } else if (storage === EStorageType.albums) {
        return getAlbumsBreadcrumbs(state);
    } else if (storage === EStorageType.incomingPublic) {
        return getIncomingPublicBreadcrumbs();
    } else if (storage === EStorageType.familyFolders) {
        return familyFoldersCrumb;
    } else if (storage === EStorageType.familyAlbums) {
        return familyAlbumsCrumb;
    } else if (storage === EStorageType.vigbo) {
        return getVigboBreadcrumbs(state);
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const list = getBreadCrumbs(state, undefined);

    if (prevBreadCrumbsList?.length === list?.length && equals(prevBreadCrumbsList, list)) {
        return prevBreadCrumbsList;
    }

    prevBreadCrumbsList = list;

    return list;
};

// eslint-disable-next-line max-lines-per-function
export const BreadcrumbsContainer = memo(
    ({
        storage,
        className,
        isSearch,
        asPageTitle = false,
    }: {
        storage?: EStorageType;
        className?: string;
        isSearch?: boolean;
        asPageTitle?: boolean;
    }) => {
        const dispatch = useDispatch();
        const list = useSelector((state: RootState) => getBreadCrumbsList(state, storage, isSearch));
        const currentStorage = useSelector(getCurrentStorage);
        const welcomePromo = useSelector(isWelcomePromoShow);
        const IsFacesFeature = useSelector(getFeatureFaces);
        const domainFoldersFilterActive = useSelector(getDomainFoldersFilterActive);
        const currentFolder = useSelector(getCurrentFolderHome);
        const integrationQueryString = useSelector(getIntegrationQueryString);
        const isFollowToSharedFolders =
            currentFolder?.kind === 'domain-folder' ||
            currentFolder?.kind === 'mounted' ||
            currentFolder?.isMounted ||
            domainFoldersFilterActive;
        const { isDomainAndShareRestricted, isDomainAndEditRestricted } = getDomainRestrictionsForItems(currentFolder);

        storage = storage || currentStorage;

        const itemPublic = useSelector(getCurrentPublicFolder);

        const currentId = path(['id'], list[list.length - 1]);

        const showCounter =
            storage === EStorageType.home ||
            storage === EStorageType.integration ||
            storage === EStorageType.inlineIntegration ||
            storage === EStorageType.sharedLinks ||
            storage === EStorageType.sharedAutodelete ||
            storage === EStorageType.public ||
            storage === EStorageType.favorites;

        const showFacesFilter = storage === EStorageType.public && IsFacesFeature;

        // tempexp_17847-start
        const isPaidUser = useSelector(UserSelectors.isPaidUser);
        const firstPublicWithDownloadLeftZero = useSelector((state: RootState) => getFirstPublicWithDownloadLeftZero(state));
        const valuePublicLimits = firstPublicWithDownloadLeftZero && Object.values(firstPublicWithDownloadLeftZero)[0];
        const leftDownloads = valuePublicLimits?.left;
        const totalDownloads = valuePublicLimits?.total;
        const isFamilyFolder = currentFolder?.kind === 'family';

        const currentFolfder = useSelector(getCurrentFolderHome);
        const isHome = currentFolfder?.id === ROOT_FOLDER_ID;
        const showDownloadLimitsInformer =
            enabledPublicDownloadLimits &&
            (storage === EStorageType.home || storage === EStorageType.sharedLinks) &&
            !isPaidUser &&
            leftDownloads === 0;
        // tempexp_17847-end

        const goToSharedFolders = useCallback(() => {
            dispatch(setDomainFoldersFilterActive(true));
        }, [dispatch]);

        const breadcrumbs = useMemo(() => {
            return list.map<BreadcrumbItem>((item) => {
                const isHome = item.storage === EStorageType.home;
                const isRoot = item.id === ROOT_FOLDER_ID;
                const isH1 = !!ALLOWED_INDEX_TITLE || (seoEnable && itemPublic?.weblinkSeoEnabled);

                const id = !isHome && isRoot ? `/${item.storage}` : item.id;

                return {
                    text: item.name,
                    id,
                    kind: item.kind,
                    showWeblink: isDomainAndShareRestricted ? false : item.showWeblink,
                    weblink: isHome ? item.weblink : '',
                    showShare: isDomainAndShareRestricted ? false : item.showShare,
                    showIcon: item.showIcon,
                    iconName: item.iconName,
                    icon: item.icon,
                    disableEdit: isDomainAndEditRestricted,
                    storage: item.storage,
                    href: item.href,
                    search: item.search || (isIntegrationStorage(storage) ? integrationQueryString : ''),
                    isH1,

                    isDomain: item.isDomain,
                    isMounted: item.isMounted,
                    isShared: item.isShared,
                    isReadOnly: item.isReadOnly,

                    parent: item.parent,
                };
            });
        }, [list, currentId, storage, domainFoldersFilterActive, isDomainAndShareRestricted, isDomainAndEditRestricted]);

        useEffect(() => {
            emitAnalyticEvent(AnalyticEventNames.BREADCRUMBS_SHOW);
            sendAnalytics('show');
        }, []);

        const handleOnShareClick = useCallback((e, breadcrumb) => {
            sendAnalytics('share-click');
            toolbarActions.share(breadcrumb.id);
        }, []);

        const handleOnPublishClick = useCallback(
            (e, breadcrumb) => {
                sendAnalytics('publish-click');
                publishHelper({ item: breadcrumb, publishFrom: 'breadcrumb' });
            },
            [breadcrumbs]
        );

        if (storage === EStorageType.start || welcomePromo) {
            return null;
        }

        return (
            <>
                <div
                    className={classNames(styles.root, className, {
                        [styles.shove]: !!PUBLIC_SHOVE,
                        [styles.rootResponsive]: ENABLE_FULL_RESPONSIVE,
                        [styles.rootIntegration]: isIntegrationStorage(storage),
                    })}
                >
                    <div
                        className={classNames(styles.breadcrumbsWrapper, {
                            [styles.breadcrumbsWrapperIntegration]: isIntegrationStorage(storage),
                        })}
                    >
                        <BreadCrumbs
                            breadcrumbs={breadcrumbs}
                            asPageTitle={asPageTitle}
                            onShareClick={handleOnShareClick}
                            onPublishClick={handleOnPublishClick}
                            storage={storage}
                            isFollowToSharedFolders={isFollowToSharedFolders}
                            goToSharedFolders={goToSharedFolders}
                        />
                        {isFamilyFolder && familyFeatureDocuments && currentFolder?.name.match(/Документы семьи/) && (
                            <div className={styles.familyDocumentsWrapper}>
                                <FamilyInlineInformer />
                            </div>
                        )}
                        {storage === EStorageType.attaches && (
                            <div className={styles.faceWrapper}>
                                <AttachesTooltip />
                            </div>
                        )}
                        {storage === EStorageType.home && Boolean(overquotaAfterClear) && !IS_DOCUMENTS_DOMAIN && !isFamilyFolder && (
                            <div className={styles.faceWrapper}>
                                <OverquotaRestoreRequestedInfo key="OverquotaRestoreRequestedInfo" />
                            </div>
                        )}
                        {showFacesFilter && !PUBLIC_SHOVE && (
                            <div className={styles.faceWrapper}>
                                <FaceFilter />
                            </div>
                        )}
                        {showDownloadLimitsInformer && isHome && <DownloadLimitsInformer totalDownloads={totalDownloads} isHome={isHome} />}
                    </div>
                    {showCounter && (
                        <div className={styles.filesCounterWrapper}>
                            <FilesCounter asPageTitle={asPageTitle} textColor={FileCounterTextColors.secondary} />
                        </div>
                    )}
                </div>
                {showFacesFilter && !!PUBLIC_SHOVE && (
                    <div
                        className={classNames(styles.root, {
                            [styles.shove]: !!PUBLIC_SHOVE,
                        })}
                    >
                        <div className={styles.faceWrapper}>
                            <FaceFilter />
                        </div>
                    </div>
                )}
            </>
        );
    }
);

BreadcrumbsContainer.displayName = 'BreadcrumbsContainer';

BreadcrumbsContainer.whyDidYouRender = true;
